// import ClassicEditor from '@rastech-inc/ckeditor';
import { MultiDatePicker_Range } from 'components/MyDatePicker/MultiDatePicker';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment-jalaali';
import React, { useEffect, useState } from 'react';
import Button from 'components/Button/Button';
import CKEditorField, {
  CkEditorBaseConfig,
} from 'components/CKEditorField/CKEditorField';
import Collapsible from 'components/Collapsible/Collapsible';
import HoverIcon from 'components/HoverIcon/HoverIcon';
import Icon from 'components/Icon/Icon';
import Input from 'components/Input/Input';
import { notifyError } from 'components/Notify/Notify';
import Select from 'components/Select';
import { IOption } from 'components/Select/Select';
import { endOfDate, startOfDate } from 'utils/date';
import { black, blue, red } from 'utils/style/colors';
import { Card } from '../CompanyPermissionSchemes/styles';
import { messages } from './Messages';
import {
  Container,
  ContentContainerColChildren,
  ContentContainerRow,
  ContentHeader,
  EditorWrapper,
  RectangleWrapper,
  Row,
} from './style';
import { convertDigitToProperFormat } from 'utils/number';
import {
  HintIcon,
  HintText,
  QuestionHintBox,
} from 'components/FileManager/ItemsViewer/Survey/SurveyEditor/GeneralQuestion/styles';
import { PDPClass, PDPProgram, Phase, Term } from 'store/pdp/types';
import { ITags } from 'store/providers/providerTypes';
import { ICompany, ICompanyRole } from 'store/companies/companiesTypes';
import { createOrUpdateTermFn } from 'store/pdp/pdpSlice';
import ButtonGroup from '../../../../components/ButtonGroup';
import Dropdown from '../../../../components/Dropdown';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';

const { Menu, MenuItem } = Dropdown;

interface ITerms {
  term?: Term;
  personPrograms: {
    [key: number]: PDPProgram;
  };
  classes: { [key: number]: PDPClass };
}

interface ITermInfo {
  id: number;
  name: string;
  rolesList: number[];
  usersList: any;
}

interface ComponentProps {
  term?: Term;
  new?: boolean;
  editable?: boolean;
  allRoleOptions: IOption[];
  selectTagOptions: IOption[];
  createOrUpdateTerm: (topic: Term) => ReturnType<typeof createOrUpdateTermFn>;
  getAllPDPInTerm: (termId: number) => Promise<PDPProgram[]>;
  getAllClassesInTerm: (termId: number) => Promise<any>;
  getMyClassesInTerm: (termId: number) => Promise<any>;
  allTags: ITags['data'];
  pdpId: number;
  company: ICompany;
  token?: string;
  terms: ITerms[];
}

function getDateFilterOptions(): IOption[] {
  const dateFilterPicker = [];
  const endOfCurrentYear = moment(endOfDate(new Date(), 'year', true)).add(
    1,
    'jYear'
  );
  const startOfCurrentYear = moment(startOfDate(new Date(), 'year', true)).add(
    1,
    'jYear'
  );
  for (let i = 0; i < 10; i++) {
    dateFilterPicker.push({
      label: convertDigitToProperFormat(startOfCurrentYear.format('jYYYY')),
      value: i,
      startDate: startOfCurrentYear.valueOf(),
      endDate: endOfCurrentYear.valueOf(),
    } as IOption);
    endOfCurrentYear.subtract(
      1,
      'jYear' as moment.unitOfTime.DurationConstructor
    );
    startOfCurrentYear.subtract(
      1,
      'jYear' as moment.unitOfTime.DurationConstructor
    );
  }
  return dateFilterPicker;
}
const dateFilterOptions: IOption[] = getDateFilterOptions();
export const TermEditor: React.FC<ComponentProps> = (props) => {
  const users = useSelector((state: RootState) => state.users.map);

  const [editing, setEditing] = useState(props.new || false);
  const [loading, setLoading] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('basicSettings');
  const [selectedPhase, setSelectedPhase] = useState(() => {
    return props?.term?.properties.phaseConfig.phases.at(0)?.id ?? '';
  });
  const [allPhaseOptions, setAllPhaseOptions] = useState(() => {
    return (
      props?.term?.properties.phaseConfig.phases.reduce((acc, phase) => {
        acc[phase.id] = phase.name;
        return acc;
      }, {}) ?? {}
    );
  });
  const now = new Date();

  const newTerm = {
    properties: {
      name: '',
      phaseConfig: {
        phases: [] as Phase[],
      },
      perTagLimits: { '-1': { max: undefined, min: 0 } },
      targetRoleIds: [] as number[],
    },
    companyPdpId: props.pdpId,
    startDate: startOfDate(
      new Date(now.getFullYear(), now.getMonth(), now.getDate()),
      'year',
      true
    ),
    endDate: endOfDate(
      new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59),
      'year',
      true
    ),
  } as Term;
  const [editingTerm, setEditingTerm] = useState(
    props.new ? newTerm : (undefined as Term | undefined)
  );

  useEffect(() => {
    setEditing(true);
    setEditingTerm(props.new ? newTerm : props.term);
  }, [props.term, props.new]);
  const term = editing ? editingTerm! : props.term || newTerm;
  const foundYear = dateFilterOptions.find((it) => {
    return (
      it.startDate >= (term?.startDate || 0) &&
      it.endDate <= (term?.endDate || 0)
    );
  });

  useEffect(() => {
    setAllPhaseOptions(() => {
      return (
        editingTerm?.properties.phaseConfig.phases.reduce((acc, phase) => {
          acc[phase.id] = phase.name === '' ? messages.newPhase : phase.name;
          return acc;
        }, {}) ?? {}
      );
    });
  }, [editingTerm]);

  const handleSelectSubject = (subject: string) => {
    setSelectedSubject(subject);
  };

  const handleSelectPhase = (phaseId: string) => {
    setSelectedPhase(phaseId);
  };

  const termInfo: ITermInfo[] = props.terms
    ?.filter(
      (item: ITerms) =>
        (item.term!.startDate
          ? moment(item.term!.startDate).format('jYYYY')
          : 0) ===
          (term.startDate ? moment(term.startDate).format('jYYYY') : 0) &&
        item.term?.id !== term.id
    )
    .reduce((acc: any, item: ITerms) => {
      if (item.term?.properties?.targetRoleIds) {
        const rolesList = item.term.properties.targetRoleIds;
        const usersList = Object.entries(props.company.roles).reduce(
          (accList: any, [key, role]) => {
            if (rolesList.includes(Number(key))) {
              accList.push(...(role as ICompanyRole).users);
            }
            return accList;
          },
          []
        );
        acc.push({
          id: item.term.id,
          name: item.term.properties.name,
          rolesList,
          usersList: Array.from(
            new Set(
              usersList.reduce(
                (acc: number[], item: any) => [item.userId, ...acc],
                []
              )
            )
          ),
        });
      }
      return acc;
    }, []);

  const duplicatedRoles = term.properties.targetRoleIds.reduce(
    (
      acc: Array<{ id: string; termName: string; roleName: string }>,
      roleId
    ) => {
      termInfo.map((otherTerm: ITermInfo) => {
        if (otherTerm.rolesList.includes(roleId)) {
          acc.push({
            id: `${otherTerm.id}-${roleId}`,
            termName: otherTerm.name,
            roleName:
              props.company.roles[roleId]?.name ||
              props.company.roles[roleId]?.displayName ||
              '',
          });
        }
      });
      return acc;
    },
    []
  );

  const termUsersList = Array.from(
    new Set(
      term.properties.targetRoleIds.reduce((acc: number[], roleId: number) => {
        const newArray = [...acc];
        Object.entries(props.company.roles).forEach(([key, role]) => {
          if (Number(key) === roleId) {
            newArray.push(
              ...Array.from(
                new Set(
                  (role as ICompanyRole).users.reduce(
                    (accUsers: number[], user) => {
                      if (!accUsers.includes(user.userId))
                        accUsers.push(user.userId);
                      return accUsers;
                    },
                    []
                  )
                )
              )
            );
          }
        });
        return newArray;
      }, [])
    )
  );

  const duplicatedUsers = termUsersList.reduce(
    (acc: Array<{ termName: string; userName: string }>, userId: number) => {
      termInfo.map((otherTerm) => {
        if (otherTerm.usersList.includes(userId))
          acc.push({ termName: otherTerm.name, userName: users[userId]?.name });
      });
      return acc;
    },
    []
  );

  const headerOptions = {
    basicSettings: messages.basicSetting,
    ...(!props.term?.properties.disableNeedAssessment
      ? { termPhases: messages.termPhases }
      : {}),
  };

  return (
    <EditorWrapper>
      <ContentHeader>
        <ButtonGroup
          items={headerOptions}
          onSelect={handleSelectSubject}
          selected={selectedSubject}
        />
        {editing && (
          <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
            {term.deleted ? (
              <Button
                color={'red-outline'}
                onClick={() => {
                  if (confirm(messages.confirmRestoreTerm)) {
                    const clone = cloneDeep(term);
                    clone.deleted = false;
                    setEditingTerm(clone);
                    props.createOrUpdateTerm(clone);
                  }
                }}
              >
                <Icon type="rotate-cw" />
                {messages.restore}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setLoading(true);
                  props
                    .createOrUpdateTerm(editingTerm!)
                    .then(() => {
                      // setEditing(false);
                    })
                    .catch((reason: any) => {
                      notifyError(reason);
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                }}
                color={'blue'}
                disabled={!term.properties.name}
                loading={loading}
              >
                {messages.save}
              </Button>
            )}
          </div>
        )}
        {editing && term.id && (
          <Dropdown
            curveBorder={true}
            marginToTarget={4}
            placement="bottom-start"
            target={
              <div>
                <HoverIcon
                  type="more-vertical"
                  titleClick={true}
                  color={black}
                />
              </div>
            }
          >
            <Menu>
              <MenuItem>
                <a
                  onClick={() => {
                    if (confirm(messages.confirmDeleteTerm)) {
                      const clone = cloneDeep(term);
                      clone.deleted = true;
                      setEditingTerm(clone);
                      props.createOrUpdateTerm(clone);
                    }
                  }}
                >
                  {' '}
                  <Icon type="trash" /> {messages.delete}
                </a>
                <a
                  onClick={() => {
                    if (confirm(messages.createDuplicateMessage)) {
                      const clone = cloneDeep(term);
                      clone.id = undefined;
                      clone.properties.name =
                        clone.properties.name + messages.copySuffix;
                      setEditingTerm(clone);
                      props.createOrUpdateTerm(clone);
                    }
                  }}
                >
                  {' '}
                  <Icon type="copy" /> {messages.createDuplicate}
                </a>
                {/*<div*/}
                {/*  style={{*/}
                {/*    display: 'flex',*/}
                {/*    flexDirection: 'row-reverse',*/}
                {/*  }}*/}
                {/*>*/}
                {/*  <Button*/}
                {/*    color={'red-outline'}*/}
                {/*    onClick={() => {*/}
                {/*      if (confirm(messages.confirmDeleteTerm)) {*/}
                {/*        const clone = cloneDeep(term);*/}
                {/*        clone.deleted = true;*/}
                {/*        setEditingTerm(clone);*/}
                {/*        props.createOrUpdateTerm(clone);*/}
                {/*      }*/}
                {/*    }}*/}
                {/*  >*/}
                {/*    <Icon type="trash" />*/}
                {/*    {messages.delete}*/}
                {/*  </Button>*/}
                {/*</div>*/}
                {/*<div*/}
                {/*  style={{*/}
                {/*    display: 'flex',*/}
                {/*    flexDirection: 'row-reverse',*/}
                {/*  }}*/}
                {/*>*/}
                {/*  <Button*/}
                {/*    color={'red-outline'}*/}
                {/*    onClick={() => {*/}
                {/*      if (confirm(messages.createDuplicateMessage)) {*/}
                {/*        const clone = cloneDeep(term);*/}
                {/*        clone.id = undefined;*/}
                {/*        clone.properties.name =*/}
                {/*          clone.properties.name + messages.copySuffix;*/}
                {/*        setEditingTerm(clone);*/}
                {/*        props.createOrUpdateTerm(clone);*/}
                {/*      }*/}
                {/*    }}*/}
                {/*  >*/}
                {/*    <Icon type="copy" />*/}
                {/*    {messages.createDuplicate}*/}
                {/*  </Button>*/}
                {/*</div>*/}
              </MenuItem>
            </Menu>
          </Dropdown>
        )}
      </ContentHeader>

      {selectedSubject === 'basicSettings' ? (
        <div
          style={{
            maxHeight: '100%',
            overflow: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
            }}
          >
            <ContentContainerRow>
              <div>
                <Input
                  id={'title' + (term?.id || 'new')}
                  type="text"
                  value={term?.properties.name}
                  onChange={(event) => {
                    const update = cloneDeep(term);
                    update!.properties.name = event.target.value;
                    setEditingTerm({ ...update! });
                  }}
                  label={messages.title}
                  disabled={!editing}
                />
              </div>
              <div>
                <Select
                  onChangeValues={(selected) => {
                    const update = cloneDeep(editingTerm!);
                    update.startDate =
                      selected.length > 0
                        ? (selected[0] as any).startDate
                        : undefined;
                    update.endDate =
                      selected.length > 0
                        ? (selected[0] as any).endDate
                        : undefined;
                    setEditingTerm(update);
                  }}
                  label={messages.year}
                  options={dateFilterOptions}
                  values={foundYear ? [foundYear] : []}
                  isRequired={false}
                  allowRemove={true}
                  disabled={!editing}
                />
              </div>
              <div>
                <Input
                  id={'assessment' + (term?.id || 'new')}
                  type="checkbox"
                  checked={!editingTerm?.properties.disableNeedAssessment}
                  onChange={(event) => {
                    const update = cloneDeep(editingTerm!);
                    update.properties.disableNeedAssessment =
                      !event.target.checked;
                    setEditingTerm(update);
                  }}
                  label={messages.assessmentStatus}
                  disabled={!editing}
                  labelStyle={{ color: black }}
                />
              </div>
              {
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  {props.term?.deleted && (
                    <span style={{ color: red }}>{messages.deleted}</span>
                  )}
                  {props.editable && !props.new && !editing && (
                    <Button
                      onClick={() => {
                        setEditing(true);
                        const toEditTerm = cloneDeep(props.term)!;
                        if (!toEditTerm.properties.perTagLimits['-1']) {
                          toEditTerm.properties.perTagLimits['-1'] = {
                            min: 0,
                            max: undefined,
                          };
                        }
                        setEditingTerm(toEditTerm);
                      }}
                    >
                      <Icon type={'edit'} /> {messages.edit}
                    </Button>
                  )}
                </div>
              }
            </ContentContainerRow>
            <ContentContainerRow
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0',
                alignItems: 'flex-start',
                margin: '0',
              }}
            >
              <Select
                disabled={!editing}
                values={props.allRoleOptions.filter((it) =>
                  term?.properties.targetRoleIds.includes(Number(it.value))
                )}
                options={props.allRoleOptions}
                multiChoice={true}
                onChangeValues={(selected) => {
                  const update = cloneDeep(term);
                  update!.properties!.targetRoleIds = selected.map((it) =>
                    Number(it.value)
                  );
                  setEditingTerm(update);
                }}
                label={messages.relatedRoles}
                containerStyle={{
                  flexGrow: '1',
                  marginLeft: '1rem',
                  width: '100%',
                }}
              />
              {(duplicatedUsers.length > 0 || duplicatedRoles.length > 0) && (
                <Collapsible
                  header={
                    <span style={{ color: red }}>
                      {messages.duplicatedUserRoles}
                    </span>
                  }
                  style={{ width: '100%' }}
                  customBodyStyle={{ maxHeight: '50vh', overflow: 'auto' }}
                >
                  {duplicatedRoles.length > 0 && (
                    <div className="flex gap-1 flex-wrap mb-2">
                      {' '}
                      <span className="font-bold">
                        {messages.duplicateRoles}:
                      </span>
                      {duplicatedRoles.map((item) => {
                        return (
                          <RectangleWrapper className="px-2" key={item.id}>
                            {messages.showDuplicate(
                              item.termName,
                              item.roleName
                            )}
                          </RectangleWrapper>
                        );
                      })}
                    </div>
                  )}
                  {duplicatedRoles.length === 0 &&
                    duplicatedUsers.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {' '}
                        <span className="font-bold">
                          {messages.duplicateUsers}:
                        </span>
                        {duplicatedUsers.map((item, ind) => {
                          return (
                            <RectangleWrapper
                              className="px-2"
                              key={`${ind}-${item}`}
                            >
                              {messages.showDuplicateUsers(
                                item.userName,
                                item.termName
                              )}
                            </RectangleWrapper>
                          );
                        })}{' '}
                      </div>
                    )}
                </Collapsible>
              )}
            </ContentContainerRow>
            <ContentContainerColChildren>
              {!props.term?.properties.disableNeedAssessment && (
                <Collapsible
                  isOpen={true}
                  header={messages.topicLimits}
                  noZ={true}
                >
                  {Object.entries(term.properties.perTagLimits)
                    .sort((a, b) => (Number(a[0]) || 0) - (Number(b[0]) || 0))
                    .map(([key, value]) => {
                      return (
                        <Card
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '1rem',
                          }}
                          key={key}
                        >
                          {key === '-1' ? (
                            <p>{messages.minMaxSelectable}</p>
                          ) : (
                            <>
                              <Select
                                containerStyle={{ minWidth: '200px' }}
                                disabled={!editing}
                                onChangeValues={(values) => {
                                  const clone = cloneDeep(term);
                                  const current =
                                    clone.properties.perTagLimits[key];
                                  delete clone.properties.perTagLimits[key];
                                  const newKey = Number(values[0]?.value || -1);
                                  clone.properties.perTagLimits[newKey] =
                                    current;
                                  setEditingTerm(clone);
                                }}
                                allowRemove={false}
                                options={props.selectTagOptions.map((it) => {
                                  return {
                                    ...it,
                                    disabled:
                                      it.disabled ||
                                      !(
                                        Number(it.value) === Number(key) ||
                                        !term.properties.perTagLimits[it.value]
                                      ),
                                  };
                                })}
                                values={props.selectTagOptions.filter(
                                  (it) => Number(key) === Number(it.value)
                                )}
                                label={messages.relatedTag}
                              />
                              <Input
                                id={'all-' + term?.id + '-' + key}
                                type="checkbox"
                                checked={value.min === -1}
                                onChange={(e) => {
                                  const clone = cloneDeep(term);
                                  clone.properties.perTagLimits[key].min = e
                                    .target.checked
                                    ? -1
                                    : 0;
                                  clone.properties.perTagLimits[key].max =
                                    undefined;
                                  setEditingTerm(clone);
                                }}
                                label={messages.selectAll}
                              />
                            </>
                          )}
                          {(value.min !== -1 || key === '-1') && (
                            <>
                              <Input
                                id={'min-' + term?.id + '-' + key}
                                type="number"
                                value={value.min}
                                onChange={(e) => {
                                  const clone = cloneDeep(term);
                                  clone.properties.perTagLimits[key].min =
                                    Number(e.target.value) || undefined;
                                  setEditingTerm(clone);
                                }}
                                label={messages.minCount}
                              />
                              <Input
                                id={'max-' + term?.id + '-' + key}
                                type="number"
                                value={value.max}
                                onChange={(e) => {
                                  const clone = cloneDeep(term);
                                  clone.properties.perTagLimits[key].max =
                                    Number(e.target.value) || undefined;
                                  setEditingTerm(clone);
                                }}
                                label={messages.maxCount}
                                placeholder={messages.noLimit}
                              />
                            </>
                          )}
                          <div style={{ flexGrow: 1 }} />
                          {editing && key !== '-1' && (
                            <HoverIcon
                              type="trash"
                              color={red}
                              onClick={() => {
                                if (confirm(messages.areYouSureDeleteLimit)) {
                                  const update = cloneDeep(editingTerm!);
                                  delete update.properties.perTagLimits[key];
                                  setEditingTerm(update);
                                }
                              }}
                            />
                          )}
                        </Card>
                      );
                    })}
                  {editing && (
                    <Button
                      id={term.id + 'addLimit'}
                      onClick={() => {
                        const update = cloneDeep(term);
                        update.properties.perTagLimits[''] = {
                          min: 0,
                          max: undefined,
                        };
                        setEditingTerm(update);
                      }}
                    >
                      {messages.addLimit}
                    </Button>
                  )}
                </Collapsible>
              )}
            </ContentContainerColChildren>
          </div>
        </div>
      ) : (
        <Container>
          <Row>
            <ButtonGroup
              items={allPhaseOptions}
              selected={selectedPhase}
              onSelect={handleSelectPhase}
              vertical={true}
            />
            {editing && (
              <Button
                style={{ marginRight: '1rem' }}
                onClick={() => {
                  const update = cloneDeep(editingTerm);
                  const id = new Date().getTime().toString();
                  update?.properties.phaseConfig.phases.push({
                    id: id,
                    name: '',
                    startDate: new Date(
                      now.getFullYear(),
                      now.getMonth(),
                      now.getDate()
                    ).getTime(),
                    endDate: new Date(
                      now.getFullYear(),
                      now.getMonth(),
                      now.getDate(),
                      23,
                      59,
                      59
                    ).getTime(),
                    validActions: [],
                    whoManagesWhom: [],
                    validTagsToSelect: [],
                  });
                  setEditingTerm(update);
                  setSelectedPhase(id);
                }}
              >
                {messages.addPhase}
              </Button>
            )}
          </Row>
          {props.editable && (
            <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
              {term.properties.phaseConfig.phases.map((phase, phaseIndex) => {
                return phase.id === selectedPhase ? (
                  <Card
                    key={phase.id}
                    style={{ height: 'calc( 100% - 20px )', overflow: 'auto' }}
                  >
                    <div style={{ maxHeight: '100%', overflow: 'auto' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '20px',
                        }}
                      >
                        <Input
                          id={'name' + phase.id}
                          type="text"
                          value={phase.name}
                          onChange={(event) => {
                            const update = cloneDeep(editingTerm!);
                            update.properties.phaseConfig.phases.find(
                              (it) => it.id === phase.id
                            )!.name = event.target.value;
                            setEditingTerm(update);
                          }}
                          label={messages.phaseTitle}
                          disabled={!editing}
                        />
                        <MultiDatePicker_Range
                          isEditable={editing}
                          label={`${messages.duration}`}
                          values={{
                            startDate: phase?.startDate,
                            dueDate: phase?.endDate,
                          }}
                          hasTime={true}
                          setDate={(values) => {
                            const update = cloneDeep(editingTerm!);
                            update.properties.phaseConfig.phases.find(
                              (it) => it.id === phase.id
                            )!.startDate = values?.startDate
                              ? Number(values.startDate)
                              : undefined;

                            const date = values.dueDate
                              ? new Date(Number(values.dueDate))
                              : undefined;
                            update.properties.phaseConfig.phases.find(
                              (it) => it.id === phase.id
                            )!.endDate = date?.getTime();
                            setEditingTerm(update);
                          }}
                        />
                        <Select
                          values={
                            phase.validTagsToSelect?.map((it) => {
                              return {
                                label: props.allTags[it]?.text,
                                value: it,
                              };
                            }) || []
                          }
                          round={true}
                          options={Object.values(props.allTags)
                            .filter(
                              (it) =>
                                props.company.pdpPanels
                                  .find((el) => el.id === props.pdpId)!
                                  .properties.topicTagsInPDP.includes(
                                    Number(it.id)
                                  ) && !it.deleted
                            )
                            .map((it) => ({
                              label: it.text,
                              value: it.id,
                            }))}
                          disabled={!editing}
                          multiChoice={true}
                          label={messages.editableFields}
                          onChangeValues={(values) => {
                            const update = cloneDeep(editingTerm!);
                            update.properties.phaseConfig.phases.find(
                              (it) => it.id === phase.id
                            )!.validTagsToSelect = values.map((it) =>
                              Number(it.value)
                            );
                            setEditingTerm(update);
                          }}
                        />
                        {/*<Input
                      id={'onlyAddTopic' + phase.id}
                      type={'checkbox'}
                      disabled={!editing}
                      label={messages.onlyAddTopic}
                      checked={
                        phase.validActions.includes('Add') &&
                        !phase.validActions.includes('Approve')
                      }
                      onChange={(e) => {
                        const newValue = e.target.checked ? ['Add'] : [];
                        const update = cloneDeep(editingTerm!);
                        update.properties.phaseConfig.phases.find(
                          (it) => it.id === phase.id
                        )!.validActions = newValue as PDPAction[];
                        setEditingTerm(update);
                      }}
                    />*/}

                        <div style={{ flexGrow: 1 }}></div>

                        {editing && (
                          <HoverIcon
                            type="trash"
                            color={red}
                            onClick={() => {
                              if (confirm(messages.areYouSureDeletePhase)) {
                                const update = cloneDeep(editingTerm!);
                                update.properties.phaseConfig.phases =
                                  update.properties.phaseConfig.phases.filter(
                                    (it) => it.id !== phase.id
                                  );
                                setEditingTerm(update);
                              }
                            }}
                          />
                        )}
                      </div>
                      <div>
                        <Input
                          id={'sendSMS'}
                          type="checkbox"
                          checked={phase.sendSms}
                          onChange={(e) => {
                            const update = cloneDeep(term);
                            update!.properties.phaseConfig.phases[
                              phaseIndex
                            ].sendSms = e.target.checked;
                            setEditingTerm(update);
                          }}
                          label={messages.sendSMSToTargetUsersInPhase}
                        />
                        {phase.startDate && phase.sendSms && (
                          <QuestionHintBox color={blue}>
                            <HintIcon type="message-square" color={blue} />
                            <HintText style={{ color: blue }}>
                              <span>{messages.notificationWillBeSent}</span>
                            </HintText>
                          </QuestionHintBox>
                        )}
                      </div>
                      {props.editable && (
                        <Collapsible
                          header={messages.whoManageWhom}
                          nested={true}
                          isOpen={true}
                          noZ={true}
                        >
                          {phase.whoManagesWhom.length === 0 && (
                            <QuestionHintBox color={blue}>
                              <HintIcon type="alert-circle" color={blue} />
                              <HintText style={{ color: blue }}>
                                <span>{messages.noRuleInfo}</span>
                              </HintText>
                            </QuestionHintBox>
                          )}
                          {phase.whoManagesWhom.map(
                            (whoManagesWhom, windex) => {
                              return (
                                <Card
                                  key={windex}
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: '20px',
                                  }}
                                >
                                  <Select
                                    disabled={!editing}
                                    label={messages.managerRole}
                                    placeholder={messages.managerRole}
                                    values={props.allRoleOptions.filter(
                                      (it) =>
                                        whoManagesWhom.managerRoleId ===
                                        Number(it.value)
                                    )}
                                    options={props.allRoleOptions}
                                    multiChoice={false}
                                    containerStyle={{ width: '200px' }}
                                    onChangeValues={(selected) => {
                                      const update = cloneDeep(term);
                                      update!.properties.phaseConfig.phases[
                                        phaseIndex
                                      ].whoManagesWhom[windex].managerRoleId =
                                        selected.map((it) =>
                                          Number(it.value)
                                        )[0];
                                      setEditingTerm(update);
                                    }}
                                  />
                                  <Select
                                    disabled={!editing}
                                    label={messages.targetRoles}
                                    placeholder={messages.targetRoles}
                                    values={props.allRoleOptions.filter((it) =>
                                      whoManagesWhom.targetRolesId.includes(
                                        Number(it.value)
                                      )
                                    )}
                                    options={props.allRoleOptions}
                                    multiChoice={true}
                                    onChangeValues={(selected) => {
                                      const update = cloneDeep(term);
                                      update!.properties.phaseConfig.phases[
                                        phaseIndex
                                      ].whoManagesWhom[windex].targetRolesId =
                                        selected.map((it) => Number(it.value));
                                      setEditingTerm(update);
                                    }}
                                  />
                                  <Input
                                    id={`${windex + '' + phaseIndex}`}
                                    type="checkbox"
                                    checked={whoManagesWhom.respectDistrict}
                                    onChange={(e) => {
                                      const update = cloneDeep(term) as Term;
                                      update.properties.phaseConfig.phases[
                                        phaseIndex
                                      ].whoManagesWhom[windex].respectDistrict =
                                        e.target.checked;
                                      setEditingTerm(update);
                                    }}
                                    label={messages.respectDistrict(
                                      props.allRoleOptions.find(
                                        (it) =>
                                          Number(it.value) ===
                                          whoManagesWhom.managerRoleId
                                      )?.label || messages.managerRole
                                    )}
                                  />
                                  <div style={{ flexGrow: 1 }} />
                                  {editing && (
                                    <HoverIcon
                                      type="trash"
                                      onClick={() => {
                                        const update = cloneDeep(term) as Term;
                                        update.properties.phaseConfig.phases[
                                          phaseIndex
                                        ].whoManagesWhom.splice(windex, 1);
                                        setEditingTerm(update);
                                      }}
                                      color={red}
                                    />
                                  )}
                                </Card>
                              );
                            }
                          )}
                          {editing && (
                            <Button
                              onClick={() => {
                                const update = cloneDeep(editingTerm);
                                update?.properties.phaseConfig.phases[
                                  phaseIndex
                                ].whoManagesWhom.push({
                                  targetRolesId: [],
                                  managerRoleId: -1,
                                  respectDistrict: false,
                                });
                                setEditingTerm(update);
                              }}
                            >
                              {messages.addManagingRole}
                            </Button>
                          )}
                        </Collapsible>
                      )}
                      <Collapsible
                        isOpen={true}
                        header={messages.phaseHint}
                        noZ={true}
                        nested={true}
                      >
                        <CKEditorField
                          style={{
                            '--ck-focus-ring': '0px',
                          }}
                          // editor={ClassicEditor}
                          data={phase.phaseHint}
                          onChange={(content: string) => {
                            const update = cloneDeep(term) as Term;
                            update.properties.phaseConfig.phases[
                              phaseIndex
                            ].phaseHint = content;
                            setEditingTerm(update);
                          }}
                          dir="rtl"
                          disabled={!editing}
                          config={CkEditorBaseConfig(props.token)}
                        />
                      </Collapsible>
                    </div>
                  </Card>
                ) : null;
              })}
            </div>
          )}
        </Container>
      )}
      {/*{props.editable && (*/}
      {/*  <Collapsible header={messages.topicLimits} nested={true}></Collapsible>*/}
      {/*)}*/}

      {!editing && (
        <div />
        // <TopicAndClasses
        //   term={term}
        //   getAllPDP={props.getAllPDPInTerm}
        //   getAllClassesInTerm={props.getAllPropsInTerm}
        //   getMyClasses={props.getMyClassesInTerm}
        // />
      )}
    </EditorWrapper>
  );
};
